import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Eye, Plus, Trash2, Edit3, Star, Bell, Copy, ExternalLink, Search, Filter, X, Loader, BookmarkPlus, Users, DollarSign, TrendingUp, Clock, Hash, Tags, RefreshCw, Check, Globe, Twitter } from 'lucide-react';
import { useWalletUser } from '../../hooks/useWalletUser';
import { adminSupabase } from '../../services/supabaseService';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import GlobalDropdown from '../../components/GlobalDropdown';
import { getTokenData, extractTokenStats, getWalletProfitAndLoss, getWalletTokensWithMetadata } from '../../services/solanatrackerService';
import { wsService } from '../../services/websocketService';

interface WatchlistToken {
  id: string;
  token_address: string;
  token_name: string | null;
  token_symbol: string | null;
  token_image_url: string | null;
  custom_name: string | null;
  notes: string | null;
  alert_price_above: number | null;
  alert_price_below: number | null;
  alert_volume_spike_percent: number;
  tags: string[];
  priority: number;
  times_viewed: number;
  created_at: string;
}

interface WatchlistWallet {
  id: string;
  watched_wallet_address: string;
  watched_wallet_name: string | null;
  alert_new_tokens: boolean;
  alert_large_trades: boolean;
  alert_trade_threshold_usd: number;
  alert_copy_trades: boolean;
  notes: string | null;
  tags: string[];
  category: string;
  priority: number;
  times_viewed: number;
  total_tracked_trades: number;
  last_trade_at: string | null;
  created_at: string;
}

// Add interface for wallet PNL data
interface WalletPnLData {
  totalPnL: number;
  totalInvested: number;
  totalValue: number;
  tokenCount: number;
  topTokens: Array<{
    symbol: string;
    balance: string;
    value: number;
    image?: string;
  }>;
}

interface SmartWatchlistProps {
  onClose?: () => void;
  isWidget?: boolean;
}

const SmartWatchlist: React.FC<SmartWatchlistProps> = ({ onClose }) => {
  const user = useWalletUser();
  const [activeTab, setActiveTab] = useState<'tokens' | 'wallets'>('tokens');
  const [tokens, setTokens] = useState<WatchlistToken[]>([]);
  const [wallets, setWallets] = useState<WatchlistWallet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<number | 'all'>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  // Add token/wallet modal states
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [customName, setCustomName] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState(1);
  const [tags, setTags] = useState('');

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WatchlistToken | WatchlistWallet | null>(null);
  const [editingType, setEditingType] = useState<'token' | 'wallet'>('token');

  // Token data and real-time states
  const [tokenDataCache, setTokenDataCache] = useState<Map<string, any>>(new Map());
  const [tokenStatsCache, setTokenStatsCache] = useState<Map<string, any>>(new Map());
  const [loadingTokens, setLoadingTokens] = useState<Set<string>>(new Set());
  const [wsSubscriptions, setWsSubscriptions] = useState<Map<string, () => void>>(new Map());
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());

  // Add PNL data states
  const [walletPnLData, setWalletPnLData] = useState<Map<string, WalletPnLData>>(new Map());
  const [loadingWalletPnL, setLoadingWalletPnL] = useState<Set<string>>(new Set());

  // Add refs for cleanup and intervals
  const refreshIntervalRef = useRef<Map<string, number>>(new Map());
  const supabaseSubscriptionsRef = useRef<any[]>([]);

  // Load watchlist data
  const loadWatchlistData = useCallback(async () => {
    if (!user?.walletAddress) return;

    try {
      setError(null);
      setIsLoading(true);

      // Fetch tokens
      const { data: tokensData, error: tokensError } = await adminSupabase
        .from('watchlist_tokens')
        .select('*')
        .eq('wallet_address', user.walletAddress)
        .order('created_at', { ascending: false });

      // Fetch wallets
      const { data: walletsData, error: walletsError } = await adminSupabase
        .from('watchlist_wallets')
        .select('*')
        .eq('wallet_address', user.walletAddress)
        .order('created_at', { ascending: false });

      if (tokensError) throw tokensError;
      if (walletsError) throw walletsError;

      setTokens(tokensData || []);
      setWallets(walletsData || []);

    } catch (err) {
      console.error('Error loading watchlist data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load watchlist data');
    } finally {
      setIsLoading(false);
    }
  }, [user?.walletAddress]);

  useEffect(() => {
    loadWatchlistData();
  }, [loadWatchlistData]);

  // Set up realtime subscriptions for automatic updates - FIXED
  useEffect(() => {
    if (!user?.walletAddress) return;

    console.log('🔄 Setting up Smart Watchlist realtime subscriptions...');

    // Clean up any existing subscriptions
    supabaseSubscriptionsRef.current.forEach(subscription => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
    supabaseSubscriptionsRef.current = [];

    // Subscribe to watchlist_tokens changes with unique channel name
    const tokensSubscription = adminSupabase
      .channel(`watchlist_tokens_${user.walletAddress}_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'watchlist_tokens',
          filter: `wallet_address=eq.${user.walletAddress}`
        },
        (payload) => {
          console.log('📋 Watchlist tokens change detected:', payload);
          
          if (payload.eventType === 'INSERT') {
            // Add new token to the list
            const newToken = payload.new as WatchlistToken;
            setTokens(prev => {
              // Check if token already exists to prevent duplicates
              const exists = prev.some(token => token.id === newToken.id);
              if (exists) return prev;
              return [newToken, ...prev];
            });
            console.log(`✅ Added new token to watchlist: ${newToken.token_address}`);
          } else if (payload.eventType === 'DELETE') {
            // Remove token from the list
            const deletedId = payload.old.id;
            setTokens(prev => {
              const filtered = prev.filter(token => token.id !== deletedId);
              console.log(`✅ Removed token from watchlist: ${deletedId}`);
              return filtered;
            });
          } else if (payload.eventType === 'UPDATE') {
            // Update existing token
            const updatedToken = payload.new as WatchlistToken;
            setTokens(prev => prev.map(token => 
              token.id === updatedToken.id ? updatedToken : token
            ));
            console.log(`✅ Updated token in watchlist: ${updatedToken.token_address}`);
          }
        }
      )
      .subscribe((status) => {
        console.log('📋 Tokens subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to watchlist_tokens changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to watchlist_tokens');
        } else if (status === 'TIMED_OUT') {
          console.warn('⏰ Watchlist_tokens subscription timed out');
        }
      });

    // Subscribe to watchlist_wallets changes with unique channel name
    const walletsSubscription = adminSupabase
      .channel(`watchlist_wallets_${user.walletAddress}_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'watchlist_wallets',
          filter: `wallet_address=eq.${user.walletAddress}`
        },
        (payload) => {
          console.log('👥 Watchlist wallets change detected:', payload);
          
          if (payload.eventType === 'INSERT') {
            // Add new wallet to the list
            const newWallet = payload.new as WatchlistWallet;
            setWallets(prev => {
              // Check if wallet already exists to prevent duplicates
              const exists = prev.some(wallet => wallet.id === newWallet.id);
              if (exists) return prev;
              return [newWallet, ...prev];
            });
            console.log(`✅ Added new wallet to watchlist: ${newWallet.watched_wallet_address}`);
          } else if (payload.eventType === 'DELETE') {
            // Remove wallet from the list
            const deletedId = payload.old.id;
            setWallets(prev => {
              const filtered = prev.filter(wallet => wallet.id !== deletedId);
              console.log(`✅ Removed wallet from watchlist: ${deletedId}`);
              return filtered;
            });
          } else if (payload.eventType === 'UPDATE') {
            // Update existing wallet
            const updatedWallet = payload.new as WatchlistWallet;
            setWallets(prev => prev.map(wallet => 
              wallet.id === updatedWallet.id ? updatedWallet : wallet
            ));
            console.log(`✅ Updated wallet in watchlist: ${updatedWallet.watched_wallet_address}`);
          }
        }
      )
      .subscribe((status) => {
        console.log('👥 Wallets subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to watchlist_wallets changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to watchlist_wallets');
        } else if (status === 'TIMED_OUT') {
          console.warn('⏰ Watchlist_wallets subscription timed out');
        }
      });

    // Store subscriptions for cleanup
    supabaseSubscriptionsRef.current = [tokensSubscription, walletsSubscription];

    // Cleanup subscriptions on unmount
    return () => {
      console.log('🧹 Cleaning up Smart Watchlist realtime subscriptions...');
      supabaseSubscriptionsRef.current.forEach(subscription => {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      });
      supabaseSubscriptionsRef.current = [];
    };
  }, [user?.walletAddress]);

  // Add new token to watchlist
  const addToken = async () => {
    if (!user?.walletAddress || !newTokenAddress) return;

    try {
      // Use INSERT and handle duplicate errors gracefully
      const { error } = await adminSupabase
        .from('watchlist_tokens')
        .insert({
          user_id: user.id,
          wallet_address: user.walletAddress,
          token_address: newTokenAddress,
          custom_name: customName || null,
          notes: notes || null,
          priority: priority,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        });

      if (error) {
        // Check if it's a duplicate error (token already in watchlist)
        if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique_user_token')) {
          setError('Token is already in your watchlist');
          return;
        }
        throw error;
      }

      // Clear form and close modal - real-time subscription will update the list
      clearFormState();
      setShowAddModal(false);

    } catch (err) {
      console.error('Error adding token:', err);
      setError(err instanceof Error ? err.message : 'Failed to add token');
    }
  };

  // Add new wallet to watchlist
  const addWallet = async () => {
    if (!user?.walletAddress || !newWalletAddress) return;

    try {
      // Use INSERT and handle duplicate errors gracefully
      const { error } = await adminSupabase
        .from('watchlist_wallets')
        .insert({
          user_id: user.id,
          wallet_address: user.walletAddress,
          watched_wallet_address: newWalletAddress,
          watched_wallet_name: customName || null,
          notes: notes || null,
          priority: priority,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          category: 'unknown',
        });

      if (error) {
        // Check if it's a duplicate error (wallet already in watchlist)
        if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique_user_watched_wallet')) {
          setError('Wallet is already in your watchlist');
          return;
        }
        throw error;
      }

      // Clear form and close modal - real-time subscription will update the list
      clearFormState();
      setShowAddModal(false);

    } catch (err) {
      console.error('Error adding wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to add wallet');
    }
  };

  // Remove token from watchlist
  const removeToken = async (tokenId: string) => {
    console.log(`🗑️ Attempting to remove token with ID: ${tokenId}`);
    try {
      // Optimistically update UI immediately
      setTokens(prev => prev.filter(token => token.id !== tokenId));
      
      const { error } = await adminSupabase
        .from('watchlist_tokens')
        .delete()
        .eq('id', tokenId);

      if (error) {
        console.error('❌ Database error removing token:', error);
        // Revert optimistic update on error
        await loadWatchlistData();
        throw error;
      }
      
      console.log(`✅ Successfully deleted token ${tokenId} from database`);
      // Real-time subscription will also update the list, but we've already done it optimistically

    } catch (err) {
      console.error('❌ Error removing token:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove token');
    }
  };

  // Remove wallet from watchlist
  const removeWallet = async (walletId: string) => {
    console.log(`🗑️ Attempting to remove wallet with ID: ${walletId}`);
    try {
      // Optimistically update UI immediately
      setWallets(prev => prev.filter(wallet => wallet.id !== walletId));
      
      const { error } = await adminSupabase
        .from('watchlist_wallets')
        .delete()
        .eq('id', walletId);

      if (error) {
        console.error('❌ Database error removing wallet:', error);
        // Revert optimistic update on error
        await loadWatchlistData();
        throw error;
      }
      
      console.log(`✅ Successfully deleted wallet ${walletId} from database`);
      // Real-time subscription will also update the list, but we've already done it optimistically

    } catch (err) {
      console.error('❌ Error removing wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove wallet');
    }
  };

  // Open edit modal
  const openEditModal = (item: WatchlistToken | WatchlistWallet, type: 'token' | 'wallet') => {
    setEditingItem(item);
    setEditingType(type);
    
    // Pre-populate form with existing data
    if (type === 'token') {
      const token = item as WatchlistToken;
      setCustomName(token.custom_name || '');
      setNotes(token.notes || '');
      setPriority(token.priority);
      setTags(token.tags.join(', '));
    } else {
      const wallet = item as WatchlistWallet;
      setCustomName(wallet.watched_wallet_name || '');
      setNotes(wallet.notes || '');
      setPriority(wallet.priority);
      setTags(wallet.tags.join(', '));
    }
    
    setShowEditModal(true);
  };

  // Update token in watchlist
  const updateToken = async () => {
    if (!editingItem || editingType !== 'token') return;

    try {
      const { error } = await adminSupabase
        .from('watchlist_tokens')
        .update({
          custom_name: customName || null,
          notes: notes || null,
          priority: priority,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          updated_at: new Date().toISOString()
        })
        .eq('id', editingItem.id);

      if (error) throw error;

      // Clear form and close modal - real-time subscription will update the list
      clearFormState();
      setEditingItem(null);
      setShowEditModal(false);

    } catch (err) {
      console.error('Error updating token:', err);
      setError(err instanceof Error ? err.message : 'Failed to update token');
    }
  };

  // Update wallet in watchlist
  const updateWallet = async () => {
    if (!editingItem || editingType !== 'wallet') return;

    try {
      const { error } = await adminSupabase
        .from('watchlist_wallets')
        .update({
          watched_wallet_name: customName || null,
          notes: notes || null,
          priority: priority,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          updated_at: new Date().toISOString()
        })
        .eq('id', editingItem.id);

      if (error) throw error;

      // Clear form and close modal - real-time subscription will update the list
      clearFormState();
      setEditingItem(null);
      setShowEditModal(false);

    } catch (err) {
      console.error('Error updating wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to update wallet');
    }
  };

  // Helper functions
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Clear form state
  const clearFormState = () => {
    setNewTokenAddress('');
    setNewWalletAddress('');
    setCustomName('');
    setNotes('');
    setPriority(1);
    setTags('');
  };

  // Close edit modal and clear state
  const closeEditModal = () => {
    clearFormState();
    setEditingItem(null);
    setShowEditModal(false);
  };

  // Fetch token data from SolanaTracker - REWRITTEN to match TokenInfo exactly
  const fetchTokenData = useCallback(async (tokenAddress: string) => {
    if (tokenDataCache.has(tokenAddress) || loadingTokens.has(tokenAddress)) {
      return tokenDataCache.get(tokenAddress);
    }

    setLoadingTokens(prev => new Set([...prev, tokenAddress]));

    try {
      console.log(`🪙 Fetching token data for ${tokenAddress} (TokenInfo method)`);
      
      // Use the same method as TokenInfo - call both getTokenInfo and getTokenData
      const [infoResponse, dataResponse] = await Promise.all([
        getTokenData(tokenAddress), // This gets the comprehensive data
        getTokenData(tokenAddress)  // This ensures we get the latest stats
      ]);
      
      // Handle the response format exactly like TokenInfo
      if (dataResponse && dataResponse.status === 'success' && dataResponse.data) {
        const data = dataResponse.data;
        
        if (data.token) {
          const tokenData = {
            name: data.token.name,
            symbol: data.token.symbol,
            image: data.token.image,
            mint: data.token.mint,
            decimals: data.token.decimals,
            description: data.token.description,
            strictSocials: data.token.strictSocials || {},
            pools: data.pools || [],
            holders: data.holders || 0,
            buys: data.buys || 0,
            sells: data.sells || 0,
            txns: data.txns || 0
          };

          setTokenDataCache(prev => new Map(prev.set(tokenAddress, tokenData)));
          
          // Extract and cache stats using the same method as TokenInfo
          const stats = extractTokenStats(dataResponse);
          if (stats) {
            setTokenStatsCache(prev => new Map(prev.set(tokenAddress, {
              ...stats,
              lastUpdated: Date.now()
            })));
            console.log(`✅ Token stats extracted for ${tokenData.symbol}:`, stats);
          } else {
            console.warn(`⚠️ Failed to extract stats for ${tokenData.symbol}`);
          }

          console.log(`✅ Token data fetched for ${tokenData.symbol || tokenAddress}`);
          return tokenData;
        } else {
          console.warn(`⚠️ No token data in response for ${tokenAddress}:`, data);
        }
      } else {
        console.warn(`⚠️ Invalid response format for ${tokenAddress}:`, dataResponse);
      }
    } catch (error) {
      console.error(`❌ Error fetching token data for ${tokenAddress}:`, error);
    } finally {
      setLoadingTokens(prev => {
        const newSet = new Set(prev);
        newSet.delete(tokenAddress);
        return newSet;
      });
    }

    return null;
  }, [tokenDataCache, loadingTokens]);

  // Setup WebSocket subscription for token - REWRITTEN to match TokenInfo exactly
  const setupTokenSubscription = useCallback(async (tokenAddress: string) => {
    if (wsSubscriptions.has(tokenAddress)) {
      return; // Already subscribed
    }

    try {
      await wsService.ensureConnection();
      
      // Use the same WebSocket pattern as TokenInfo
      console.log(`🔄 Setting up WebSocket subscription for ${tokenAddress} (TokenInfo method)`);
      
      // Join the token room (same as TokenInfo)
      wsService.joinRoom(`token:${tokenAddress}`);

      // Listen for token updates (same as TokenInfo)
      const handleTokenUpdate = (data: any) => {
        console.log(`💰 Token update for ${tokenAddress}:`, data);
        
        // Update token stats cache with new information
        setTokenStatsCache(prev => {
          const currentStats = prev.get(tokenAddress) || {};
          const updatedStats = {
            ...currentStats,
            price: data.price?.usd || currentStats.price,
            marketCap: data.marketCap?.usd || currentStats.marketCap,
            liquidity: data.liquidity?.usd || currentStats.liquidity,
            lastUpdated: Date.now()
          };
          
          return new Map(prev.set(tokenAddress, updatedStats));
        });
      };

      // Subscribe to token updates (same as TokenInfo)
      wsService.on(`token:${tokenAddress}`, handleTokenUpdate);
      
      // Store cleanup function
      const unsubscribe = () => {
        // Don't leave room - might be used by token feeds
        // wsService.leaveRoom(`token:${tokenAddress}`);
        console.log(`🧹 Unsubscribed from WebSocket events for ${tokenAddress} (keeping room)`);
      };

      setWsSubscriptions(prev => new Map(prev.set(tokenAddress, unsubscribe)));
      console.log(`✅ WebSocket subscription setup for ${tokenAddress}`);
    } catch (error) {
      console.error(`❌ Failed to setup WebSocket for ${tokenAddress}:`, error);
    }
  }, [wsSubscriptions]);

  // Setup auto-refresh intervals for token stats - REWRITTEN to match TokenInfo (1-second intervals)
  const setupTokenAutoRefresh = useCallback((tokenAddress: string) => {
    // Clear any existing interval for this token
    const existingInterval = refreshIntervalRef.current.get(tokenAddress);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Set up a new interval to refresh token stats every 1 second (same as TokenInfo)
    const intervalId = window.setInterval(async () => {
      try {
        // Only refresh if the token is still in our watchlist and visible
        const isTokenVisible = tokens.some(token => token.token_address === tokenAddress);
        if (!isTokenVisible) {
          // Clean up interval if token is no longer visible
          clearInterval(intervalId);
          refreshIntervalRef.current.delete(tokenAddress);
          return;
        }

        // Use the same refresh method as TokenInfo (silent refresh)
        const response = await getTokenData(tokenAddress);
        const stats = extractTokenStats(response);
        if (stats) {
          setTokenStatsCache(prev => new Map(prev.set(tokenAddress, {
            ...stats,
            lastUpdated: Date.now()
          })));
          // Don't log every auto-refresh to avoid spam
        }
      } catch (error) {
        console.error(`❌ Error auto-refreshing stats for ${tokenAddress}:`, error);
      }
    }, 1000); // 1 second interval (same as TokenInfo)

    refreshIntervalRef.current.set(tokenAddress, intervalId);
    console.log(`⏰ Auto-refresh setup for ${tokenAddress} (1-second intervals)`);
  }, [tokens]);

  // Lazy load token data on demand - NEW APPROACH
  const loadTokenDataOnDemand = useCallback(async (tokenAddress: string) => {
    if (!tokenDataCache.has(tokenAddress) && !loadingTokens.has(tokenAddress)) {
      // Fetch data, setup subscription, and auto-refresh in parallel
      const promises = [
        fetchTokenData(tokenAddress),
        setupTokenSubscription(tokenAddress)
      ];
      
      await Promise.allSettled(promises);
      
      // Setup auto-refresh after initial data is loaded
      setupTokenAutoRefresh(tokenAddress);
    }
  }, [fetchTokenData, setupTokenSubscription, setupTokenAutoRefresh, tokenDataCache, loadingTokens]);

  // Cleanup WebSocket subscriptions and intervals
  useEffect(() => {
    return () => {
      // Clean up WebSocket subscriptions (same as TokenInfo)
      wsSubscriptions.forEach((unsubscribe, tokenAddress) => {
        unsubscribe(); // This calls wsService.leaveRoom()
        console.log(`🧹 Cleaned up WebSocket subscription for: ${tokenAddress}`);
      });
      
      // Clean up auto-refresh intervals
      refreshIntervalRef.current.forEach((intervalId, tokenAddress) => {
        clearInterval(intervalId);
        console.log(`🧹 Cleaned up auto-refresh for: ${tokenAddress}`);
      });
      refreshIntervalRef.current.clear();
      
      console.log('🧹 SmartWatchlist: Cleaned up all WebSocket subscriptions and intervals');
    };
  }, [wsSubscriptions]);

  // Clean up subscriptions for removed tokens - ENHANCED to match TokenInfo pattern
  useEffect(() => {
    const currentTokenAddresses = new Set(tokens.map(t => t.token_address));
    
    // Clean up WebSocket subscriptions for removed tokens (same as TokenInfo)
    wsSubscriptions.forEach((unsubscribe, tokenAddress) => {
      if (!currentTokenAddresses.has(tokenAddress)) {
        unsubscribe(); // This calls wsService.leaveRoom()
        setWsSubscriptions(prev => {
          const newMap = new Map(prev);
          newMap.delete(tokenAddress);
          return newMap;
        });
        console.log(`🧹 Cleaned up WebSocket subscription for removed token: ${tokenAddress}`);
      }
    });

    // Clean up auto-refresh intervals for removed tokens
    refreshIntervalRef.current.forEach((intervalId, tokenAddress) => {
      if (!currentTokenAddresses.has(tokenAddress)) {
        clearInterval(intervalId);
        refreshIntervalRef.current.delete(tokenAddress);
        console.log(`🧹 Cleaned up auto-refresh for removed token: ${tokenAddress}`);
      }
    });

    // Clean up cache for removed tokens to free memory
    setTokenDataCache(prev => {
      const newMap = new Map();
      let removedCount = 0;
      prev.forEach((data, tokenAddress) => {
        if (currentTokenAddresses.has(tokenAddress)) {
          newMap.set(tokenAddress, data);
        } else {
          removedCount++;
      }
    });
      if (removedCount > 0) {
        console.log(`🧹 Cleaned up token data cache for ${removedCount} removed tokens`);
      }
      return newMap;
    });

    setTokenStatsCache(prev => {
      const newMap = new Map();
      let removedCount = 0;
      prev.forEach((stats, tokenAddress) => {
        if (currentTokenAddresses.has(tokenAddress)) {
          newMap.set(tokenAddress, stats);
        } else {
          removedCount++;
        }
      });
      if (removedCount > 0) {
        console.log(`🧹 Cleaned up token stats cache for ${removedCount} removed tokens`);
      }
      return newMap;
    });

  }, [tokens, wsSubscriptions]);

  // Enhanced copy to clipboard with feedback
  const copyToClipboardWithFeedback = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItems(prev => new Set([...prev, text]));
    setTimeout(() => {
      setCopiedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(text);
        return newSet;
      });
    }, 2000);
  }, []);

  // Refresh token data
  const refreshTokenData = useCallback(async (tokenAddress: string) => {
    // Keep existing stats while refreshing to prevent clearing the display
    const existingStats = tokenStatsCache.get(tokenAddress);
    
    // Remove from token data cache to force refresh
    setTokenDataCache(prev => {
      const newMap = new Map(prev);
      newMap.delete(tokenAddress);
      return newMap;
    });
    
    // Don't clear stats cache - let the new fetch update it or preserve existing
    // This prevents the statistics from disappearing during refresh

    // Fetch fresh data
    const newTokenData = await fetchTokenData(tokenAddress);
    
    // If fetch failed and we had existing stats, make sure they're still there
    if (!newTokenData && existingStats) {
      setTokenStatsCache(prev => new Map(prev.set(tokenAddress, existingStats)));
    }
    
    return newTokenData;
  }, [fetchTokenData, tokenStatsCache]);

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / 60000);
    
    if (diffInMins < 1) return 'Just now';
    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInMins < 1440) return `${Math.floor(diffInMins / 60)}h ago`;
    return `${Math.floor(diffInMins / 1440)}d ago`;
  };

  // Helper functions for formatting numbers
  const formatLargeNumber = (num: number | null | undefined, decimals = 2): string => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    
    const absNum = Math.abs(num);
    if (absNum >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(decimals)}M`;
    } else if (absNum >= 1_000) {
      return `${(num / 1_000).toFixed(decimals)}K`;
    } else if (absNum < 0.01 && num !== 0) {
      return num.toFixed(6);
    } else {
      return num.toFixed(decimals);
    }
  };

  // Format currency with appropriate precision
  const formatCurrency = (amount: number): string => {
    if (Math.abs(amount) >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(2)}M`;
    } else if (Math.abs(amount) >= 1_000) {
      return `$${(amount / 1_000).toFixed(2)}K`;
    } else if (Math.abs(amount) < 0.01 && amount !== 0) {
      return `$${amount.toFixed(6)}`;
    } else {
      return `$${amount.toFixed(2)}`;
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return 'text-[#24c35b] bg-[#24c35b]/20';
      case 2: return 'text-blue-400 bg-blue-600/20';
      case 3: return 'text-yellow-400 bg-yellow-600/20';
      case 4: return 'text-orange-400 bg-orange-600/20';
      case 5: return 'text-red-400 bg-red-600/20';
      default: return 'text-[#24c35b] bg-[#24c35b]/20';
    }
  };

  // Filter data
  const filteredTokens = tokens.filter(token => {
    const matchesSearch = !searchTerm || 
      token.token_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      token.token_symbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      token.custom_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      token.token_address.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPriority = filterPriority === 'all' || token.priority === filterPriority;
    
    return matchesSearch && matchesPriority;
  });

  const filteredWallets = wallets.filter(wallet => {
    const matchesSearch = !searchTerm || 
      wallet.watched_wallet_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.watched_wallet_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPriority = filterPriority === 'all' || wallet.priority === filterPriority;
    
    return matchesSearch && matchesPriority;
  });

  // TokenRow component with lazy loading - NEW
  const TokenRow: React.FC<{ token: WatchlistToken }> = ({ token }) => {
    const [isVisible, setIsVisible] = useState(false);
    const rowRef = useRef<HTMLDivElement>(null);

    // Intersection Observer for lazy loading
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            // Load token data when it becomes visible
            loadTokenDataOnDemand(token.token_address);
          }
        },
        { threshold: 0.1 } // Load when 10% visible
      );

      if (rowRef.current) {
        observer.observe(rowRef.current);
      }

      return () => {
        if (rowRef.current) {
          observer.unobserve(rowRef.current);
        }
      };
    }, [token.token_address, isVisible]);

                const tokenData = tokenDataCache.get(token.token_address);
                const tokenStats = tokenStatsCache.get(token.token_address);
                const isLoading = loadingTokens.has(token.token_address);
                const isCopied = copiedItems.has(token.token_address);

                return (
      <div ref={rowRef} key={token.id} className="p-2 rounded border border-[#24c35b]/20 bg-white hover:bg-[#24c35b]/5 transition-all shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      {tokenData?.image ? (
                        <img
                          src={tokenData.image}
                          alt={tokenData.symbol || 'Token'}
                          className="w-6 h-6 rounded-full border border-white/10"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-6 h-6 bg-[#24c35b]/10 rounded-full flex items-center justify-center border border-[#24c35b]/20">
                          {isLoading ? (
                            <Loader className="w-3 h-3 animate-spin text-[#24c35b]" />
                          ) : (
                            <Hash className="w-3 h-3 text-[#24c35b]" />
                          )}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-1">
                          <h3 className="font-medium text-black text-xs">
                  {token.custom_name || tokenData?.name || 'Loading...'}
                          </h3>
                          <span className="text-[10px] text-[#24c35b]">
                  ({tokenData?.symbol || 'LOADING'})
                          </span>
                          <div className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${getPriorityColor(token.priority)}`}>
                            P{token.priority}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 mt-0.5">
                          <span className="text-[10px] text-black/60 font-mono">
                            {formatAddress(token.token_address)}
                          </span>
                          <button
                            onClick={() => copyToClipboardWithFeedback(token.token_address)}
                            className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                            title={isCopied ? 'Copied!' : 'Copy address'}
                          >
                            {isCopied ? (
                              <Check className="w-2.5 h-2.5 text-green-400" />
                            ) : (
                              <Copy className="w-2.5 h-2.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] text-black/60">
                        {formatTimeAgo(token.created_at)}
                      </span>
                      <button
                        onClick={() => refreshTokenData(token.token_address)}
                        className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                        title="Refresh token data"
                        disabled={isLoading}
                      >
                        <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => openEditModal(token, 'token')}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="Edit token"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeToken(token.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Remove token"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

        {/* Real-time Token Statistics - Only show if data is loaded */}
                  {tokenStats && (
                    <div className="mb-1 p-1.5 bg-[#24c35b]/5 rounded border border-[#24c35b]/20">
                      <div className="grid grid-cols-3 gap-2 text-[10px]">
                        <div className="text-center">
                          <div className="text-[#24c35b]">Price</div>
                          <div className="text-black font-mono">
                            {formatCurrency(tokenStats.price)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#24c35b]">MCap</div>
                          <div className="text-black font-mono">
                            {formatCurrency(tokenStats.marketCap)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#24c35b]">Liq</div>
                          <div className="text-black font-mono">
                            {formatCurrency(tokenStats.liquidity)}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[10px] mt-1">
                        <div className="text-center">
                          <div className="text-[#24c35b]">Buys</div>
                          <div className="text-green-400 font-mono">
                            {formatLargeNumber(tokenStats.transactions?.buys)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#24c35b]">Sells</div>
                          <div className="text-red-400 font-mono">
                            {formatLargeNumber(tokenStats.transactions?.sells)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#24c35b]">Holders</div>
                          <div className="text-black font-mono">
                            {formatLargeNumber(tokenStats.holders)}
                          </div>
                        </div>
                      </div>
                      {tokenStats.lastUpdated && (
                        <div className="text-center mt-1 pt-1 border-t border-[#24c35b]/20">
                          <span className="text-[9px] text-black/60">
                            Updated {formatTimeAgo(new Date(tokenStats.lastUpdated).toISOString())}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

        {/* Loading placeholder for stats */}
        {isVisible && !tokenStats && isLoading && (
          <div className="mb-1 p-1.5 bg-[#24c35b]/5 rounded border border-[#24c35b]/20">
            <div className="flex items-center justify-center py-2">
              <Loader className="w-4 h-4 animate-spin text-[#24c35b] mr-2" />
              <span className="text-[10px] text-black/60">Loading statistics...</span>
            </div>
                    </div>
                  )}

                  {token.notes && (
                    <div className="text-[10px] text-black/60 mb-1">
                      {token.notes}
                    </div>
                  )}

                  {token.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {token.tags.map((tag, index) => (
                        <span key={index} className="px-1.5 py-0.5 rounded text-[9px] bg-blue-500/20 text-blue-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
            {/* Social Links - Only show if token data is loaded */}
                      {tokenData?.strictSocials?.website && (
                        <a
                          href={tokenData.strictSocials.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                          title="Website"
                        >
                          <Globe className="w-2.5 h-2.5" />
                        </a>
                      )}
                      {tokenData?.strictSocials?.twitter && (
                        <a
                          href={tokenData.strictSocials.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                          title="Twitter"
                        >
                          <Twitter className="w-2.5 h-2.5" />
                        </a>
                      )}
                      <a
                        href={`https://solscan.io/token/${token.token_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                        title="View on Solscan"
                      >
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </div>
                </div>
    );
  };

  // Fetch wallet PNL data from SolanaTracker
  const fetchWalletPnLData = useCallback(async (walletAddress: string) => {
    if (walletPnLData.has(walletAddress) || loadingWalletPnL.has(walletAddress)) {
      return walletPnLData.get(walletAddress);
    }

    setLoadingWalletPnL(prev => new Set([...prev, walletAddress]));

    try {
      // Fetch both PNL data and token metadata
      const [pnlResult, tokensResult] = await Promise.all([
        getWalletProfitAndLoss(walletAddress, false),
        getWalletTokensWithMetadata(walletAddress)
      ]);

      let pnlData: WalletPnLData = {
        totalPnL: 0,
        totalInvested: 0,
        totalValue: 0,
        tokenCount: 0,
        topTokens: []
      };

      // Process PNL data
      if (pnlResult.status === 'success' && pnlResult.data) {
        const summary = pnlResult.data.summary;
        if (summary) {
          pnlData.totalPnL = summary.total || 0;
          pnlData.totalInvested = summary.totalInvested || 0;
        }
      }

      // Process token data
      if (tokensResult.status === 'success' && tokensResult.data && tokensResult.data.tokens) {
        const tokens = tokensResult.data.tokens;
        pnlData.totalValue = tokensResult.data.total || 0;
        pnlData.tokenCount = tokens.length;

        // Get top 3 tokens by value
        pnlData.topTokens = tokens
          .filter((token: any) => token.value > 0)
          .sort((a: any, b: any) => b.value - a.value)
          .slice(0, 3)
          .map((token: any) => ({
            symbol: token.symbol || 'Unknown',
            balance: parseFloat(token.balance).toFixed(4),
            value: token.value,
            image: token.image
          }));
      }

      setWalletPnLData(prev => new Map([...prev, [walletAddress, pnlData]]));
      return pnlData;

    } catch (error) {
      console.error(`Error fetching PNL data for wallet ${walletAddress}:`, error);
      
      // Set empty data on error
      const emptyData: WalletPnLData = {
        totalPnL: 0,
        totalInvested: 0,
        totalValue: 0,
        tokenCount: 0,
        topTokens: []
      };
      
      setWalletPnLData(prev => new Map([...prev, [walletAddress, emptyData]]));
      return emptyData;
    } finally {
      setLoadingWalletPnL(prev => {
        const newSet = new Set(prev);
        newSet.delete(walletAddress);
        return newSet;
      });
    }
  }, [walletPnLData, loadingWalletPnL]);

  // Load PNL data for all wallets when wallets change
  useEffect(() => {
    if (wallets.length > 0) {
      wallets.forEach(wallet => {
        fetchWalletPnLData(wallet.watched_wallet_address);
      });
    }
  }, [wallets, fetchWalletPnLData]);

  return (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="smart-watchlist">
      <style>{globalWidgetTextStyles}</style>
      
      {/* Header - Homepage styling */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <BookmarkPlus size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Smart Watchlist</h2>
          {user?.walletAddress && (
            <div className="flex items-center ml-2">
              <div className="w-2 h-2 rounded-full bg-[#24c35b] animate-pulse"></div>
              <span className="text-xs text-[#24c35b] ml-1">Live</span>
            </div>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors p-1 rounded hover:bg-[#24c35b]/5"
            title="Close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Search Bar - Homepage styling */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#24c35b]" />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-white border border-[#24c35b]/20 rounded-lg text-black placeholder-[#24c35b]/60 text-sm focus:outline-none focus:border-[#24c35b] hover:border-[#24c35b]/40"
          />
        </div>
      </div>

      {/* Filter Bar - Homepage style */}
      <div className="p-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-[#24c35b]" />
              <GlobalDropdown
                options={[
                  { value: 'all', label: 'All Priorities' },
                  { value: '1', label: 'Low (1)' },
                  { value: '2', label: 'Medium (2)' },
                  { value: '3', label: 'High (3)' },
                  { value: '4', label: 'Critical (4)' },
                  { value: '5', label: 'Emergency (5)' }
                ]}
                value={filterPriority.toString()}
                onChange={(value) => setFilterPriority(value === 'all' ? 'all' : Number(value))}
                placeholder="Select priority"
              />
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="px-3 py-2 rounded-lg bg-[#24c35b] text-white text-sm hover:bg-[#20a855] transition-all flex items-center shadow-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Tabs - Homepage style */}
        <div className="flex space-x-1 bg-white rounded-lg p-1 border border-gray-200">
          <button
            onClick={() => setActiveTab('tokens')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'tokens'
                ? 'bg-[#24c35b] text-white shadow-sm'
                : 'text-black hover:text-[#24c35b] hover:bg-[#24c35b]/5'
            }`}
          >
            <Hash className="w-4 h-4 inline mr-1" />
            Tokens ({tokens.length})
          </button>
          <button
            onClick={() => setActiveTab('wallets')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'wallets'
                ? 'bg-[#24c35b] text-white shadow-sm'
                : 'text-black hover:text-[#24c35b] hover:bg-[#24c35b]/5'
            }`}
          >
            <Users className="w-4 h-4 inline mr-1" />
            Wallets ({wallets.length})
          </button>
        </div>
      </div>

      {/* Content - White background styling */}
      <div className="flex-grow overflow-y-auto px-2 py-1.5 bg-white" style={{ borderRadius: 0, overflowX: 'visible' }}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-600 text-sm font-medium mb-1">Error Loading Data</div>
            <p className="text-red-500 text-xs">{error}</p>
          </div>
        )}

        {activeTab === 'tokens' ? (
          <div className="space-y-1">
            {filteredTokens.length === 0 ? (
              <div className="text-center py-8">
                <Hash className="w-12 h-12 text-[#24c35b] mx-auto mb-3" />
                <p className="text-black/60 text-sm">
                  {searchTerm ? 'No tokens found matching your search' : 'No tokens in watchlist'}
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-3 px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
                >
                  Add your first token
                </button>
              </div>
            ) : (
              filteredTokens.map((token) => (
                <TokenRow key={token.id} token={token} />
              ))
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredWallets.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-[#24c35b] mx-auto mb-3" />
                <p className="text-black/60 text-sm">
                  {searchTerm ? 'No wallets found matching your search' : 'No wallets in watchlist'}
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-3 px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
                >
                  Add your first wallet
                </button>
              </div>
            ) : (
              filteredWallets.map((wallet) => {
                const pnlData = walletPnLData.get(wallet.watched_wallet_address);
                const isLoadingPnL = loadingWalletPnL.has(wallet.watched_wallet_address);
                const isCopied = copiedItems.has(wallet.watched_wallet_address);

                return (
                <div key={wallet.id} className="p-2 rounded border border-[#24c35b]/20 bg-white hover:bg-[#24c35b]/5 transition-all shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center border border-green-500/40">
                        <Users className="w-3 h-3 text-green-400" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-1">
                          <h3 className="font-medium text-black text-xs">
                            {wallet.watched_wallet_name || 'Unnamed Wallet'}
                          </h3>
                          <span className="text-[10px] text-[#24c35b] capitalize">
                            ({wallet.category})
                          </span>
                          <div className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${getPriorityColor(wallet.priority)}`}>
                            P{wallet.priority}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 mt-0.5">
                          <span className="text-[10px] text-black/60 font-mono">
                            {formatAddress(wallet.watched_wallet_address)}
                          </span>
                          <button
                            onClick={() => copyToClipboardWithFeedback(wallet.watched_wallet_address)}
                            className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                          >
                            {isCopied ? (
                              <Check className="w-2.5 h-2.5 text-green-400" />
                            ) : (
                              <Copy className="w-2.5 h-2.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] text-black/60">
                        {formatTimeAgo(wallet.created_at)}
                      </span>
                      <button
                        onClick={() => fetchWalletPnLData(wallet.watched_wallet_address)}
                        className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                        title="Refresh wallet data"
                        disabled={isLoadingPnL}
                      >
                        <RefreshCw className={`w-3 h-3 ${isLoadingPnL ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => openEditModal(wallet, 'wallet')}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="Edit wallet"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeWallet(wallet.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Remove wallet"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* PNL Data Section */}
                  {isLoadingPnL ? (
                    <div className="mb-2 p-2 bg-[#24c35b]/5 rounded border border-[#24c35b]/20">
                      <div className="flex items-center justify-center py-1">
                        <Loader className="w-3 h-3 animate-spin text-[#24c35b] mr-2" />
                        <span className="text-[10px] text-black/60">Loading wallet data...</span>
                      </div>
                    </div>
                  ) : pnlData ? (
                    <div className="mb-2 p-2 bg-[#24c35b]/5 rounded border border-[#24c35b]/20">
                      <div className="grid grid-cols-3 gap-2 text-[10px] mb-2">
                        <div className="text-center">
                          <div className="text-[#24c35b]">P&L</div>
                          <div className={`font-mono ${pnlData.totalPnL >= 0 ? 'text-[#24c35b]' : 'text-red-400'}`}>
                            {formatCurrency(pnlData.totalPnL)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#24c35b]">Value</div>
                          <div className="text-black font-mono">
                            {formatCurrency(pnlData.totalValue)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#24c35b]">Tokens</div>
                          <div className="text-black font-mono">
                            {pnlData.tokenCount}
                          </div>
                        </div>
                      </div>
                      
                      {/* Top Tokens */}
                      {pnlData.topTokens.length > 0 && (
                        <div className="border-t border-[#24c35b]/20 pt-1">
                          <div className="text-[9px] text-[#24c35b] mb-1">Top Holdings:</div>
                          <div className="space-y-1">
                            {pnlData.topTokens.map((token, index) => (
                              <div key={index} className="flex items-center justify-between text-[9px]">
                                <div className="flex items-center space-x-1">
                                  {token.image && (
                                    <img 
                                      src={token.image} 
                                      alt={token.symbol} 
                                      className="w-3 h-3 rounded-full"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  )}
                                  <span className="text-black">{token.symbol}</span>
                                  <span className="text-black/60">{token.balance}</span>
                                </div>
                                <div className="text-black/60">{formatCurrency(token.value)}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-2 p-2 bg-[#24c35b]/5 rounded border border-[#24c35b]/20">
                      <div className="text-center text-[10px] text-black/60">
                        No wallet data available
                      </div>
                    </div>
                  )}

                  {wallet.notes && (
                    <div className="text-[10px] text-black/60 mb-1">
                      {wallet.notes}
                    </div>
                  )}

                  {wallet.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {wallet.tags.map((tag, index) => (
                        <span key={index} className="px-1.5 py-0.5 rounded text-[9px] bg-blue-500/20 text-blue-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-[10px]">
                      {wallet.alert_new_tokens && <span className="text-green-400">New Tokens</span>}
                      {wallet.alert_large_trades && <span className="text-yellow-400">Large Trades</span>}
                      {wallet.alert_copy_trades && <span className="text-blue-400">Copy Trading</span>}
                    </div>
                    <a
                      href={`https://solscan.io/account/${wallet.watched_wallet_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#24c35b] hover:text-[#24c35b]/80 transition-colors"
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Add Modal - SniperBot styling */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-96 max-w-[90vw] border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-black font-medium">
                Add {activeTab === 'tokens' ? 'Token' : 'Wallet'} to Watchlist
              </h3>
              <button
                onClick={() => {
                  clearFormState();
                  setShowAddModal(false);
                }}
                className="text-gray-600 hover:text-black transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  {activeTab === 'tokens' ? 'Token Address' : 'Wallet Address'}
                </label>
                <input
                  type="text"
                  placeholder={activeTab === 'tokens' ? 'Enter token address...' : 'Enter wallet address...'}
                  value={activeTab === 'tokens' ? newTokenAddress : newWalletAddress}
                  onChange={(e) => activeTab === 'tokens' ? setNewTokenAddress(e.target.value) : setNewWalletAddress(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Custom Name</label>
                <input
                  type="text"
                  placeholder="Optional custom name..."
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Notes</label>
                <textarea
                  placeholder="Optional notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Priority</label>
                  <GlobalDropdown
                    options={[
                      { value: '1', label: 'Low (1)' },
                      { value: '2', label: 'Medium (2)' },
                      { value: '3', label: 'High (3)' },
                      { value: '4', label: 'Critical (4)' },
                      { value: '5', label: 'Emergency (5)' }
                    ]}
                    value={priority.toString()}
                    onChange={(value) => setPriority(Number(value))}
                    placeholder="Select priority"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Tags</label>
                  <input
                    type="text"
                    placeholder="tag1, tag2, tag3"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full bg-gray-600/5 border border-gray-600/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  clearFormState();
                  setShowAddModal(false);
                }}
                className="px-3 py-1.5 rounded border border-gray-300 text-gray-600 text-sm hover:text-black transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={activeTab === 'tokens' ? addToken : addWallet}
                disabled={!(activeTab === 'tokens' ? newTokenAddress : newWalletAddress)}
                className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-sm hover:bg-[#24c35b]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Watchlist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - SniperBot styling */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-96 max-w-[90vw] border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-black font-medium">
                Edit {editingType === 'token' ? 'Token' : 'Wallet'}
              </h3>
              <button
                onClick={closeEditModal}
                className="text-gray-600 hover:text-black transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Show address (read-only) */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  {editingType === 'token' ? 'Token Address' : 'Wallet Address'}
                </label>
                <div className="w-full bg-gray-50 border border-gray-200 rounded px-3 py-2 text-gray-600 text-sm font-mono break-all">
                  {editingType === 'token' 
                    ? (editingItem as WatchlistToken).token_address 
                    : (editingItem as WatchlistWallet).watched_wallet_address
                  }
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Custom Name</label>
                <input
                  type="text"
                  placeholder="Optional custom name..."
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Notes</label>
                <textarea
                  placeholder="Optional notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Priority</label>
                  <GlobalDropdown
                    options={[
                      { value: '1', label: 'Low (1)' },
                      { value: '2', label: 'Medium (2)' },
                      { value: '3', label: 'High (3)' },
                      { value: '4', label: 'Critical (4)' },
                      { value: '5', label: 'Emergency (5)' }
                    ]}
                    value={priority.toString()}
                    onChange={(value) => setPriority(Number(value))}
                    placeholder="Select priority"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Tags</label>
                  <input
                    type="text"
                    placeholder="tag1, tag2, tag3"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full bg-gray-600/5 border border-gray-600/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={closeEditModal}
                className="px-3 py-1.5 rounded border border-gray-300 text-gray-600 text-sm hover:text-black transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingType === 'token' ? updateToken : updateWallet}
                className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-sm hover:bg-[#24c35b]/30 transition-all"
              >
                Update {editingType === 'token' ? 'Token' : 'Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartWatchlist;

// Widget factory function
export const createSmartWatchlistWidget = () => ({
  id: 'smart-watchlist',
  name: 'Smart Watchlist',
  title: 'Smart Watchlist',
  type: 'smart-watchlist-widget',
  content: <SmartWatchlist />,
  size: { width: 600, height: 500 },
  minSize: { width: 400, height: 300 },
  maxSize: { width: 800, height: 700 },
  category: 'Analytics',
  glassMorphism: true,
  containerStyle: {
    backgroundColor: "rgba(12, 20, 26, 0.7)",
    backdropFilter: "blur(6px)",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
  },
}); 